import React, { useEffect, useState } from 'react'
import KontenCard from '../../customer/konten/KontenCard';
import Heading from '../../../component/Heading/Heading';
import { formatAmount, formatGermanAmount, formatPlainAmount, formatUpdateNumber, handleKeyPress } from "../../../helper/formatAmount";
import { Badge, Grid } from '@mui/material';
import { addDarlehenData, kontencardColors } from "../../../content/content";
import { kontenChartcolor } from "../../../content/content";
import { t } from "i18next";
import { addSelectCustomerDarlehen, deleteSelectCustomerDarlehen, getSelectCustomerDarlehen, updateSelectCustomerDarlehen } from '../../../services/admin-services/selectCustomerKontenServices';
import Toast from '../../../component/toastify/Index';
import DeleteConfirmationModal from '../../../component/model/DeleteConfirmationModel';
import CommonModal from "../../../component/model/ModelComponent";
import CommonButton from "../../../component/button/Button";
import InputComponent from "../../../component/input/Input";
import { FormControl, FormLabel, MenuItem, Select, FormHelperText, Typography } from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CommonChart from '../../../component/chart/CommonChart';
import { DemoContainer, DemoItem } from '@mui/x-date-pickers/internals/demo';
import { DateField, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import KontenNoDataPage from '../../customer/konten/NoDataPage';

const DarlehenComponent = ({ konten, setKonten, isModalOpen, setIsModalOpen, clientId }) => {
    const [formData, setFormData] = useState({});
    const [errors, setErrors] = useState({});
    const [open, setOpen] = useState(false);
    const [debouncedValues, setDebouncedValues] = useState({
        ursprungsbetrag: '',
        monatliche_rate: '',
        ausstehender_betrag: ''
    });
    const [timer, setTimer] = useState(null);

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === 'ursprungsbetrag' || name === 'monatliche_rate' || name === 'ausstehender_betrag') {
            // Immediately set the raw value in the form state to avoid losing user input while typing
            setFormData((prev) => ({ ...prev, [name]: value }));

            // Clear the previous timer if it's still active
            if (timer) {
                clearTimeout(timer);
            }

            // Set a new timer for debouncing the formatted value update
            const newTimer = setTimeout(() => {
                // Format the value only after the debounce delay
                const formattedValue = formatGermanAmount(value);
                setDebouncedValues((prev) => ({ ...prev, [name]: formattedValue }));  // Update debounced value after the delay
            }, 500); // 500 ms debounce delay

            setTimer(newTimer);
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
    };

    useEffect(() => {
        const debounceTime = setTimeout(() => {
            // Update the form state after the debounce delay with the formatted values
            setFormData((prev) => ({
                ...prev,
                ursprungsbetrag: debouncedValues.ursprungsbetrag,
                monatliche_rate: debouncedValues.monatliche_rate,
                ausstehender_betrag: debouncedValues.ausstehender_betrag
            }));
        }, 1000); // 2000 ms debounce delay for saving the formatted value

        return () => clearTimeout(debounceTime);
    }, [debouncedValues]);

    const getDarlehen = async () => {
        const response = await getSelectCustomerDarlehen(clientId);
        if (response?.isSuccess) {
            setKonten([...response.customerDarlehen])
        }
    }

    useEffect(() => {
        getDarlehen()
    }, [])

    const validate = () => {
        const newErrors = {};
        let valid = true;

        addDarlehenData.forEach((item) => {
            const value = String(formData[item.name])?.trim();
            if (!value || value === "undefined") {
                newErrors[item.name] = `${t(item.translationKey)} ${t("common_is_required")}`;
                valid = false;
            } else if (item.name === "iban") {
                if (!/^[A-Z]{2}\d{2}[A-Z0-9]{10,30}$/.test(formData.iban)) {
                    newErrors[item.name] = "IBAN should be a valid IBAN format";
                    valid = false;
                } else if (formData.iban.length > 22) {
                    newErrors[item.name] = t('IBAN_22_characters_long_error');
                    valid = false;

                }
            }
        });

        setErrors(newErrors);
        return valid;
    };

    const handleEdit = (item) => {
        item.zahlungsbeginn = dayjs(item.zahlungsbeginn);
        item.zahlungsende = dayjs(item.zahlungsende);

        // Format the fields using formatUpdateNumber
        const ursprungsbetragFormat = formatUpdateNumber(item.ursprungsbetrag);
        const monatliche_rateFormat = formatUpdateNumber(item.monatliche_rate);
        const ausstehender_betragFormat = formatUpdateNumber(item.ausstehender_betrag);

        // Update form data with formatted values
        setFormData({
            ...item,
            ursprungsbetrag: ursprungsbetragFormat,
            monatliche_rate: monatliche_rateFormat,
            ausstehender_betrag: ausstehender_betragFormat
        });

        // Set debounced values with the formatted values
        setDebouncedValues({
            ursprungsbetrag: ursprungsbetragFormat,
            monatliche_rate: monatliche_rateFormat,
            ausstehender_betrag: ausstehender_betragFormat
        });

        setErrors({});
        setIsModalOpen(true);
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        if (!validate()) return;

        formData.ursprungsbetrag = formatPlainAmount(formData.ursprungsbetrag)
        formData.monatliche_rate = formatPlainAmount(formData.monatliche_rate)
        formData.ausstehender_betrag = formatPlainAmount(formData.ausstehender_betrag)

        let response = [];
        if (formData._id) {
            response = await updateSelectCustomerDarlehen(formData, clientId);
        }
        else {
            formData.isManual = true;
            response = await addSelectCustomerDarlehen(formData, clientId);
        }
        if (response?.isSuccess) {
            Toast(response?.message, "success");
            setIsModalOpen(false)
            getDarlehen();
            setFormData({})
        }
    }

    const handleDelete = async () => {
        const payload = {
            ...formData,
            ursprungsbetrag: formatPlainAmount(formData?.ursprungsbetrag),
            monatliche_rate: formatPlainAmount(formData?.monatliche_rate),
            ausstehender_betrag: formatPlainAmount(formData?.ausstehender_betrag),
        }
        const response = await deleteSelectCustomerDarlehen(payload, clientId);
        if (response?.isSuccess) {
            Toast(response?.message, "success")
            getDarlehen();
            setFormData({})
            setOpen(false);
            setIsModalOpen(false)
        }
    }

    const handleDateChange = (name, newValue) => {
        setFormData({
            ...formData,
            [name]: newValue,
        });
    };

    const ursprungsbetragchartData = {
        series: konten.map((item) => Number(item.ursprungsbetrag)),
    }

    const monatlicheRatechartData = {
        series: konten.map((item) => Number(item.monatliche_rate)),
    }

    const ausstehenderBetragchartData = {
        series: konten.map((item) => Number(item.ausstehender_betrag)),
    }

    const options = {
        chart: {
            type: "donut",
            height: "75%"
        },
        series: konten.map((item) => item.kontostand),
        labels: konten.map((item) => item.bank_name),
        colors: kontenChartcolor,
        fill: {
            type: "gradient",
        },
        legend: {
            show: false,
        },
        dataLabels: {
            enabled: false,
        },
        stroke: { width: 0 },
        plotOptions: {
            pie: {
                expandOnClick: false,
                donut: {
                    size: "80%",
                    labels: {
                        show: true,
                        name: {
                            show: false,
                        },
                        total: {
                            show: true,
                            showAlways: true,
                            fontWeight: 500,
                            formatter: function (w) {
                                return (
                                    formatAmount(
                                        w.globals.seriesTotals.reduce((a, b) => a + b, 0)
                                    ) + " €"
                                );
                            },
                        },
                    },
                },
            },
        },
        states: {
            active: {
                allowMultipleDataPointsSelection: false,
                filter: {
                    type: "none",
                },
            },
        },
        tooltip: {
            enabled: true,
            style: {
                fontFamily: 'Poppins',
            },
            y: {
                formatter: function (val, opts) {
                    return formatAmount(val) + " €";
                },
            },
        },
        responsive: [
            {
                breakpoint: 10000,
                options: {
                    chart: {
                        height: "65%"
                    },
                    plotOptions: {
                        pie: {
                            donut: {
                                labels: {
                                    value: {
                                        fontSize: '2vh',
                                    },
                                },
                            },
                        },
                    },

                    tooltip: {
                        style: {
                            fontSize: "1vh",
                        },
                    },
                },
            },
            {
                breakpoint: 6000,
                options: {
                    plotOptions: {
                        pie: {
                            donut: {
                                labels: {
                                    value: {
                                        fontSize: '1.5vh',
                                    },
                                },
                            },
                        },
                    },
                    tooltip: {
                        style: {
                            fontSize: "1vh",
                        },
                    },
                },
            },
            {
                breakpoint: 5000,
                options: {
                    chart: {
                        height: "70%"
                    },
                    plotOptions: {
                        pie: {
                            donut: {
                                labels: {
                                    value: {
                                        fontSize: '1.5vh',
                                    },
                                },
                            },
                        },
                    },
                    tooltip: {
                        style: {
                            fontSize: "1vh",
                        },
                    },
                },
            },
            {
                breakpoint: 3000,
                options: {
                    chart: {
                        height: "70%"
                    },
                    plotOptions: {
                        pie: {
                            donut: {
                                labels: {
                                    value: {
                                        fontSize: '1.5vh',
                                    },
                                },
                            },
                        },
                    },
                    tooltip: {
                        style: {
                            fontSize: "1vh",
                        },
                    },
                },
            },
            {
                breakpoint: 2500,
                options: {
                    chart: {
                        height: "70%"
                    },
                    plotOptions: {
                        pie: {
                            donut: {
                                labels: {
                                    value: {
                                        fontSize: '1vh',
                                    },
                                },
                            },
                        },
                    },
                    tooltip: {
                        style: {
                            fontSize: "1vh",
                        },
                    },
                },
            },
            {
                breakpoint: 2000,
                options: {
                    chart: {
                        height: "75%",
                    },
                    plotOptions: {
                        pie: {
                            donut: {
                                labels: {
                                    value: {
                                        fontSize: '1.2vh',
                                    },
                                },
                            },
                        },
                    },
                    tooltip: {
                        style: {
                            fontSize: "1.3vh",
                        },
                    },
                },
            },

            {
                breakpoint: 1024,
                options: {
                    plotOptions: {
                        pie: {
                            donut: {
                                labels: {
                                    value: {
                                        fontSize: '16px',
                                    },
                                },
                            },
                        },
                    },
                    tooltip: {
                        style: {
                            fontSize: "9px",
                        },
                    },
                },
            },
            {
                breakpoint: 480,
                options: {
                    plotOptions: {
                        pie: {
                            donut: {
                                labels: {
                                    value: {
                                        fontSize: '10px',
                                    },
                                },
                            },
                        },
                    },
                    tooltip: {
                        style: {
                            fontSize: "5px",
                        },
                    },

                },
            },
        ],
    };

    return (
        <div className="darlehen-content">
            <DeleteConfirmationModal open={open}
                onClose={() => setOpen(false)}
                onClick={handleDelete}
            />
            <CommonModal
                open={isModalOpen}
                onClose={() => { setIsModalOpen(false); setFormData({}); setErrors({}); setDebouncedValues({}) }}
                title="Konten"
                className=""
            >
                <Typography
                    className="user-model"
                    sx={{
                        border: "1px solid #E3E3E3",
                        padding: "20px",
                        borderRadius: "10px",
                    }}
                >
                    <form onSubmit={handleSubmit} className="user-form">
                        <div className="konten-form-data">
                            {
                                addDarlehenData.map((item) => (
                                    item.type === "select" ? (
                                        <FormControl fullWidth error={!!errors[item.name]}>
                                            <FormLabel
                                                className="label"
                                                sx={{
                                                    paddingTop: "1.25rem",
                                                    paddingBottom: "10px",
                                                    display: "inline-block",
                                                    color: "#000",
                                                }}
                                            >
                                                {t(item.translationKey)}
                                            </FormLabel>
                                            <Select
                                                className="select-input"
                                                fullWidth
                                                value={formData[item?.name] || ""}
                                                onChange={handleChange}
                                                displayEmpty
                                                inputProps={{
                                                    name: item?.name,
                                                    IconComponent: () => (
                                                        <span
                                                            style={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                paddingRight: "10px",
                                                            }}
                                                        >
                                                            <ExpandMoreIcon sx={{ fontSize: "30px", color: "#33333399" }} />
                                                        </span>
                                                    ),
                                                }}
                                                sx={{
                                                    fontSize: "14px",
                                                    color: "#313131",
                                                    fontFamily: "poppins"
                                                }}
                                                style={{
                                                    padding: "1px",
                                                    borderRadius: "10px",
                                                    border: "1px solid #e3e3e391",
                                                }}
                                                disabled={item?.name === "year"}
                                                MenuProps={{
                                                    PaperProps: {
                                                        style: {
                                                            maxHeight: 150,
                                                            overflowY: 'auto',
                                                        },
                                                    },
                                                }}
                                            >
                                                <MenuItem value="" disabled
                                                    sx={{
                                                        fontFamily: "poppins",
                                                        color: "#000",
                                                        fontSize: "14px"
                                                    }}
                                                >
                                                    {t(item.placeholderTranslationKey)}
                                                </MenuItem>
                                                {item?.options?.map((option, i) => (
                                                    <MenuItem key={i} value={option.value} disabled={option.value === "2023"} sx={{
                                                        color: "#000",
                                                    }}>
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            {errors[item.name] && (
                                                <FormHelperText
                                                    sx={{
                                                        color: "#d32f2f",
                                                        margin: "5px 0 0 0 ",
                                                        fontSize: "14px",
                                                        fontFamily: "Poppins",
                                                    }}
                                                >
                                                    {errors[item.name]}
                                                </FormHelperText>
                                            )}
                                        </FormControl>
                                    ) : item.type == "date" ?
                                        <div className="stammdaten-datepicker">
                                            <DemoContainer components={['DateField', 'DateField']}>
                                                <LocalizationProvider
                                                    dateAdapter={AdapterDayjs}
                                                    adapterLocale="de"
                                                    localeText={{
                                                        field: { datePlaceholder: t(item.placeholderTranslationKey) },
                                                    }}
                                                >
                                                    <DemoItem sx={{ width: "100%" }}>
                                                        <Typography htmlFor="" className="grundungjahr-label" sx={{ fontFamily: "poppins", fontSize: "14px", paddingTop: "10px" }}>
                                                            {t(item.translationKey)}
                                                        </Typography>
                                                        <DateField
                                                            value={formData[item.name] || null}
                                                            name={item.name}
                                                            onChange={(e) => handleDateChange(item.name, e)}
                                                            format="DD.MM.YYYY"
                                                            error={!!errors[item.name]}
                                                            helperText={errors[item.name] || ""}
                                                            FormHelperTextProps={{
                                                                sx: {
                                                                    color: !!errors[item.name] ? "#d32f2f" : "#14A9E2",
                                                                    margin: "5px 0 0 0 ",
                                                                    fontSize: "14px",
                                                                    fontFamily: "Poppins",

                                                                },
                                                            }}
                                                            sx={{
                                                                "& .MuiOutlinedInput-root": {
                                                                    borderRadius: "10px",
                                                                    width: "100%"
                                                                },

                                                                "& input": {
                                                                    padding: "9px 12px",
                                                                    fontSize: "16px",
                                                                    fontFamily: "Poppins",
                                                                },
                                                            }}
                                                        />
                                                    </DemoItem>
                                                </LocalizationProvider>
                                            </DemoContainer>
                                        </div>
                                        :
                                        (
                                            <InputComponent
                                                key={item?.name}
                                                borderColor="E3E3E3"
                                                border="2px solid #E3E3E3"
                                                padding="9px 12px"
                                                type={item.type}
                                                placeholder={t(item.placeholderTranslationKey)}
                                                label={t(item.translationKey)}
                                                className="user-input"
                                                name={item?.name}
                                                value={formData[item?.name] || ""}
                                                error={!!errors[item.name]}
                                                helperText={errors[item.name]}
                                                onChange={handleChange}
                                                handleKeyPress={item?.handleKeyPress ? handleKeyPress : null}
                                            />
                                        )
                                ))
                            }

                        </div>
                        <Typography className="model-action-btn">
                            <CommonButton
                                text={formData._id ? "Delete" : t("user_clear_all")}
                                className="invitation-btn konten-btn"
                                bgColor="linear-gradient(90deg, #E14A4A 0%, #FF4848 100%);"
                                onClick={formData._id ? () => setOpen(true) : () => { setFormData({}); setDebouncedValues({}); }}
                            />
                            <CommonButton
                                type="submit"
                                text={formData._id ? "Update" : "Save"}
                                className="add-user-btn konten-btn"
                            />
                        </Typography>
                    </form>
                </Typography>
            </CommonModal>

            {
                konten?.length > 0
                    ? <>
                        <div className="darlehin-charts">
                            <Grid container rowSpacing={{ xs: 1, sm: 2, md: 0 }} columnSpacing={{ xs: 1, sm: 2, md: 3 }} className="chart-height">
                                <Grid size={{ xs: 12, md: 6, lg: 4 }} className="darlehen-chart h-100">
                                    <Heading text={t('module_ursprungsbetrag')} className="konten-chart-heading" />
                                    <CommonChart
                                        options={options}
                                        series={ursprungsbetragchartData.series}
                                        type="donut"
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6, lg: 4 }} className="darlehen-chart h-100">
                                    <Heading text={t('darlehen_monatliche_rate')} className="konten-chart-heading" />
                                    <CommonChart
                                        options={options}
                                        series={monatlicheRatechartData.series}
                                        type="donut"
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6, lg: 4 }} className="darlehen-chart h-100 ausstehender_chart">
                                    <Heading text={t('darlehen_ausstehender_betrag')} className="konten-chart-heading " />
                                    <CommonChart
                                        options={options}
                                        series={ausstehenderBetragchartData.series}
                                        type="donut"
                                    />
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <div className="darlehen-legends">
                                        <ul className="dalheren-cards-legends">
                                            {
                                                konten.map((item, index) => {
                                                    return <li className="dalheren-cards-legends-data">
                                                        <Badge
                                                            overlap="circular"
                                                            badgeContent=" "
                                                            variant="dot"
                                                            sx={{
                                                                "& .MuiBadge-dot": {
                                                                    backgroundColor: kontenChartcolor[index % kontencardColors.length],
                                                                    padding: ".6vh",
                                                                    borderRadius: "50%",
                                                                    color: "#ff5733",
                                                                },
                                                            }}
                                                        >
                                                        </Badge>
                                                        <div className="legends-text">{item.bank_name}</div>
                                                    </li>
                                                })
                                            }
                                        </ul>
                                    </div>
                                </Grid>
                            </Grid>
                        </div>
                        <div className="darlehin-cards">
                            <Grid container rowSpacing={{ xs: 2, sm: 2, md: 3 }} columnSpacing={{ xs: 1, sm: 2, md: 3 }} >
                                <KontenCard konten={konten} handleEdit={handleEdit} size={{ xs: 12, md: 6, lg: 4, xl: 3, xxl: 2, xxxl: 1 }} />
                            </Grid>
                        </div>
                    </>
                    : <KontenNoDataPage />
            }


        </div>
    )
}

export default DarlehenComponent
